/**
 * Settings card "إدارة الصناديق" (C20, B3 rest — D04 §5 "Box management", D-072, D-077, D-129 `settings_boxes`):
 * binds `BoxesCard` + `AddBoxDialog` / `EditBoxDialog` (packages/ui, App.tsx:14961-15034, 57068-57260) to the C19
 * routes `POST /boxes` (opening balance posted as `capital_add` in the same transaction), `PATCH /boxes` (rename keeps
 * the id) and `POST /boxes/archive` (archive at zero / restore). Every change needs the user's own PIN at the route
 * (D-018). Rows: the IQD and USD vaults first, then the digital boxes in card order (old `sortedWallets`); archived
 * boxes are listed under them with a restore button (D-072).
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AddBoxDialog, BoxesCard, EditBoxDialog, SettingsCardFrame, formatWithCommas, parseCommas, toEnglishDigits, type BoxRow } from "@faylasuf/ui";
import type { AccountRow, PinProof } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";

export function BoxesContainer({ customIcon }: { customIcon?: string }) {
  const { t, source } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const gate = usePinGate();
  const q = useQuery({ queryKey: [...keys.accounts, "with-archived"], queryFn: () => source.accounts({ kinds: ["cash_vault", "digital_box"], archived: true }) });
  if (q.error && isSessionLost(q.error)) sessionLost();
  const all: readonly AccountRow[] = q.data ?? [];

  const [adding, setAdding] = useState<{ name: string; openingBalance: string } | null>(null);
  const [editing, setEditing] = useState<{ id: string; newName: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const toRow = (a: AccountRow): BoxRow => ({ id: a.id, name: a.name ?? "", kind: a.kind === "cash_vault" ? "vault" : "digital", archived: a.archived });
  const vaults = all.filter((a) => a.kind === "cash_vault" && !a.archived);
  const boxes = all.filter((a) => a.kind === "digital_box").slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const rows = [...vaults, ...boxes.filter((b) => !b.archived)].map(toRow);
  const archivedRows = boxes.filter((b) => b.archived).map(toRow);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.accounts });
    void qc.invalidateQueries({ queryKey: keys.dashboard });
  };
  const run = async (action: (proof: PinProof) => Promise<unknown>, message: string, successKey: string): Promise<boolean> => {
    setBusy(true);
    try {
      const r = await gate.withPin(action, { message });
      if (r === undefined) return false;
      toast(t(successKey), "success");
      refresh();
      return true;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (!adding) return;
    const name = adding.name.trim();
    const opening = toEnglishDigits(parseCommas(adding.openingBalance.trim()));
    const key = crypto.randomUUID();
    const ok = await run(
      (proof) => source.createBox({ name, ...(opening && opening !== "0" ? { openingBalance: opening } : {}), idempotencyKey: key, pin: proof.pin ?? "" }),
      t("pin.message_box_create", { name }),
      "toast.box_created",
    );
    if (ok) setAdding(null);
  };
  const rename = async () => {
    if (!editing) return;
    const cur = all.find((a) => a.id === editing.id);
    const name = editing.newName.trim();
    const ok = await run((proof) => source.updateBox({ accountId: editing.id, name, pin: proof.pin ?? "" }), t("pin.message_box_rename", { from: cur?.name ?? "", to: name }), "toast.box_renamed");
    if (ok) setEditing(null);
  };
  const archive = (id: string, on: boolean) => {
    const cur = all.find((a) => a.id === id);
    void run((proof) => source.archiveBox(id, on, proof.pin ?? ""), t(on ? "pin.message_box_archive" : "pin.message_box_restore", { name: cur?.name ?? "" }), on ? "toast.box_archived" : "toast.box_restored");
  };

  const editingRow = editing ? all.find((a) => a.id === editing.id) : undefined;
  return (
    <SettingsCardFrame cardKey="wallets" {...(customIcon ? { customIcon } : {})}>
      <BoxesCard
        rows={rows}
        archivedRows={archivedRows}
        onEdit={(id) => setEditing({ id, newName: all.find((a) => a.id === id)?.name ?? "" })}
        onArchive={(id) => archive(id, true)}
        onRestore={(id) => archive(id, false)}
        onAdd={() => setAdding({ name: "", openingBalance: "" })}
      />
      <AddBoxDialog
        open={adding !== null}
        name={adding?.name ?? ""}
        openingBalance={adding?.openingBalance ?? ""}
        onChange={(p) => setAdding((a) => (a ? { ...a, ...p } : a))}
        onSubmit={() => void create()}
        onClose={() => setAdding(null)}
        busy={busy}
      />
      <EditBoxDialog
        open={editing !== null}
        currentName={editingRow?.name ?? ""}
        newName={editing?.newName ?? ""}
        balanceLabel={formatWithCommas(editingRow ? (editingRow.kind === "cash_vault" ? (editingRow.total ?? editingRow.balance) : editingRow.balance) : "0")}
        kind={editingRow?.kind === "cash_vault" ? "vault" : "digital"}
        onNewNameChange={(v) => setEditing((e) => (e ? { ...e, newName: v } : e))}
        onSubmit={() => void rename()}
        onClose={() => setEditing(null)}
        busy={busy}
      />
      {gate.dialog}
    </SettingsCardFrame>
  );
}
